<template>
  <Modal :open="open" size="big" @close="$emit('close')">
    <div>
      <h1 class="text-2xl font-bold">Analyse IA de la pièce d'identité</h1>
      <p v-if="analyzedAt" class="mt-1 text-xs text-gray-500">Analysée le {{ formattedAnalyzedAt }}</p>
    </div>

    <div v-if="errorMessage" class="mt-6 rounded-md border-l-4 border-red-400 bg-red-50 p-4 text-sm text-red-700">
      <p class="font-semibold">Analyse indisponible</p>
      <p class="mt-1">{{ errorMessage }}</p>
    </div>

    <div
      v-else-if="!analyzedAt"
      class="mt-6 rounded-md border-l-4 border-gray-300 bg-gray-50 p-4 text-sm text-gray-700"
    >
      Pièce d'identité pas encore analysée.
    </div>

    <div v-else class="mt-6 space-y-6">
      <div v-if="anomalies.length > 0" class="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        <p class="font-semibold">Anomalies détectées</p>
        <ul class="mt-2 list-disc space-y-1 pl-5">
          <li v-for="(anomaly, index) in anomalies" :key="index">{{ anomaly }}</li>
        </ul>
      </div>

      <div class="flex items-center gap-3">
        <span class="text-sm font-semibold text-gray-700">Verdict global :</span>
        <span :class="verdictBadgeClass" class="inline-flex items-center rounded-full px-3 py-1 text-xs font-bold">
          {{ verdictLabel(t, verdict) }}
        </span>
      </div>

      <div v-if="extractedFields.length > 0">
        <h2 class="text-sm font-semibold text-gray-800">Informations extraites</h2>
        <dl class="mt-2 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
          <div v-for="field in extractedFields" :key="field.label" class="flex gap-2 text-sm">
            <dt class="font-medium text-gray-500">{{ field.label }} :</dt>
            <dd class="text-gray-800">{{ field.value || "—" }}</dd>
          </div>
        </dl>
      </div>

      <VerificationTable v-if="globalChecks.length > 0" title="Vérifications globales" :checks="globalChecks" />

      <template v-if="samePerson">
        <VerificationTable
          v-if="samePersonChecks.length > 0"
          title="Souscripteur (= déclarant)"
          description="Comparaison de la pièce d'identité avec les informations à la souscription et à la déclaration"
          :checks="samePersonChecks"
        />
      </template>
      <template v-else>
        <VerificationTable
          v-if="policyHolderChecks.length > 0"
          title="Souscripteur"
          description="Comparaison de la pièce d'identité avec les informations renseignées à la souscription"
          :checks="policyHolderChecks"
        />

        <VerificationTable
          v-if="declarantChecks.length > 0"
          title="Déclarant"
          description="Comparaison de la pièce d'identité avec les informations renseignées à la déclaration du sinistre"
          :checks="declarantChecks"
        />
      </template>

      <AiAnalysisFeedbackForm :analysable-type="analysableType" :analysable-id="analysableId" analysis-type="id_card" />
    </div>

    <div v-if="claimActive && (analyzedAt || errorMessage)" class="mt-4 border-t border-gray-100 pt-4">
      <button
        type="button"
        :disabled="retrying"
        class="inline-flex items-center rounded-md bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 focus:outline-hidden disabled:opacity-50"
        @click="retryAnalysis"
      >
        {{ t("retry_button") }}
      </button>
    </div>
  </Modal>
</template>

<script>
import { useI18n } from "vue-i18n";
import { Modal } from "@estaly/ui";
import { useRetryAnalysis } from "@/composables/useRetryAnalysis";
import {
  verdictLabel,
  badgeClassFor,
  mapExtractedFields,
  mapGlobalChecks,
  mapPersonChecks,
  mapAnomalies,
  isSamePerson,
  formatAnalyzedAt,
} from "@/components/claim/details/idCardAnalysisFormatter";
import VerificationTable from "@/components/claim/details/VerificationTable.vue";
import AiAnalysisFeedbackForm from "@/components/claim/details/AiAnalysisFeedbackForm.vue";

export default {
  name: "IdCardAnalysisModal",
  components: { Modal, VerificationTable, AiAnalysisFeedbackForm },
  setup(props, { emit }) {
    const { t } = useI18n();
    const { retrying, retryAnalysis } = useRetryAnalysis("id_card");
    return { t, retrying, retryAnalysis: () => retryAnalysis(props.analysableId, emit) };
  },
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    analysis: {
      type: Object,
      default: null,
    },
    analyzedAt: {
      type: [String, Date],
      default: null,
    },
    analysableId: {
      type: Number,
      required: true,
    },
    analysableType: {
      type: String,
      required: true,
    },
    claimActive: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["close", "retry-success"],
  computed: {
    errorMessage() {
      return this.analysis?.error || null;
    },
    verdict() {
      return this.analysis?.overallVerdict || null;
    },
    extractedFields() {
      return mapExtractedFields(this.t, this.analysis);
    },
    globalChecks() {
      return mapGlobalChecks(this.t, this.analysis);
    },
    policyHolderChecks() {
      return mapPersonChecks(this.t, this.analysis, "policy_holder");
    },
    declarantChecks() {
      return mapPersonChecks(this.t, this.analysis, "declarant");
    },
    samePersonChecks() {
      return this.policyHolderChecks.length > 0 ? this.policyHolderChecks : this.declarantChecks;
    },
    anomalies() {
      return mapAnomalies(this.analysis);
    },
    samePerson() {
      return isSamePerson(this.analysis);
    },
    verdictBadgeClass() {
      return badgeClassFor(this.verdict);
    },
    formattedAnalyzedAt() {
      return formatAnalyzedAt(this.analyzedAt);
    },
  },
  methods: {
    verdictLabel,
    badgeClassFor,
  },
};
</script>

<i18n src="./IdCardAnalysisModal.i18n.json"></i18n>
